<template>
  <div class="select-creator-step">
    <h1 class="global-h1 select-creator-step__title">Who would you like to work with?</h1>

    <Loader v-if="newCustomCollabWizardStore.isLoadingCreators" />

    <!-- Select creator form -->
    <div v-show="!newCustomCollabWizardStore.isLoadingCreators" class="select-creator-form select-creator-step__select-creator-form">
      <div class="select-creator-form__title-and-content-section">
        <!-- <div v-if="isValidationErrorNotesVisible" class="global-error-string">Please select or invite a creator</div> -->
        <div class="global-h3 select-creator-form__title">Select a creator</div>

        <!-- "No selected creators" placeholder -->
        <div v-if="!newCustomCollabWizardStore.creatorsToDisplay?.length" class="no-selected-creators-placeholder select-creator-form__no-selected-creators-placeholder">
          <div class="no-selected-creators-placeholder__description">Work with any creators you have saved.</div>
          <LinkWithIcon class="no-selected-creators-placeholder__link" tag="RouterLink" :to="{ name: 'CustomerSavedCreators', params: { pageId: route.params.communityId } }" color="blue">
            <span>Go to Saved Creators page</span>
            <template #iconRight><IconEmbedded name="caret-right_3" :size="14" /></template>
          </LinkWithIcon>
        </div>
        <!-- / "No selected creators" placeholder -->

        <div
          v-else
          :class="{
            'select-creator-form__creators-list-wrap': true,
            'select-creator-form__creators-list-wrap--with-top-curtain': curtainsVisibility?.isTop?.value,
            'select-creator-form__creators-list-wrap--with-bottom-curtain': curtainsVisibility?.isBottom?.value,
          }"
        >
          <div class="select-creator-form__creators-list" ref="domRefCreatorsList">
            <!-- Creator snippet -->
            <label
              :class="{
                'creator-snippet': true,
                'creator-snippet--active': creator.shrpaCreatorId === newCustomCollabWizardStore.selectedCreatorId,
                'select-creator-form__creator-snippet': true,
              }"
              v-for="creator in newCustomCollabWizardStore.creatorsToDisplay"
              :key="creator.shrpaCreatorId"
            >
              <input
                class="creator-snippet__checkbox"
                name="creator"
                type="radio"
                :value="creator.shrpaCreatorId"
                v-model="newCustomCollabWizardStore.selectedCreatorId"
                @focus="
                  () => {
                    isValidationErrorNotesVisible = false;
                    newCustomCollabWizardStore.invitedCreatorEmail = '';
                  }
                "
              />
              <AvatarWithFallback class="creator-snippet__avatar" :src="`${contentBaseUri}/cms/images/profiles/${creator.profilePhoto}`" />
              <span class="global-h6 creator-snippet__name">
                {{ creator.name }}
              </span>
            </label>
            <!-- / Creator snippet -->
          </div>
        </div>
      </div>

      <div class="select-creator-form__or-divider"></div>

      <div class="select-creator-form__title-and-content-section">
        <div class="global-h3 select-creator-form__title">Invite a creator</div>

        <input
          class="global-text-input select-creator-form__invite-email-input"
          type="text"
          placeholder="user@example.com"
          v-model="newCustomCollabWizardStore.invitedCreatorEmail"
          @focus="
            () => {
              isValidationErrorNotesVisible = false;
              newCustomCollabWizardStore.selectedCreatorId = '';
            }
          "
          @input="
            () => {
              isValidationErrorNotesVisible = false;
              newCustomCollabWizardStore.selectedCreatorId = '';
            }
          "
        />
      </div>
      <div v-if="isValidationErrorNotesVisible" class="global-error-string">Please select or invite a creator</div>
    </div>
    <!-- / Select creator form -->

    <h3 class="global-h3" style="margin-bottom: 15px">How it works</h3>
    <HowItWorksText class="select-creator-step__how-it-works" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { emailValidationRegExps } from "@helpers/EmailHelper";
import { useRoute } from "vue-router";

// Components
import NoteWithIcon from "@components/NoteWithIcon.vue";
import AvatarWithFallback from "@components/AvatarWithFallback.vue";
import Loader from "@components/Loader/Loader.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Composables
import useScrollCurtains from "@composables/useScrollCurtains";

// Global variables & injections
const contentBaseUri = globalThis.Bootstrap.Config.contentCdnBaseUri;

// Stores
import { useNewCustomCollabWizardStore } from "@stores/newCustomCollabWizardStore";
import HowItWorksText from "@views/CustomCollabs/CustomerCustomCollabDetails/HowItWorksText.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
const newCustomCollabWizardStore = useNewCustomCollabWizardStore();

// Router
const route = useRoute();

// Creators list curtains =====================================================
const domRefCreatorsList = ref<HTMLElement | null>(null);

const curtainsVisibility = useScrollCurtains(domRefCreatorsList);

watch(
  () => newCustomCollabWizardStore.savedCreatorsPayload,
  async () => {
    await nextTick();
    curtainsVisibility.setCurtainsVisibility();
  }
);

// Is Form valid ==============================================================
const isValidationErrorNotesVisible = ref<boolean>(false);

// Define expose ==============================================================
defineExpose({
  setSelectedCreator: creatorId => (newCustomCollabWizardStore.selectedCreatorId = creatorId),
  showValidationErrorNotes: () => (isValidationErrorNotesVisible.value = true),
});
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// "No selected creators" placeholder =========================================
.no-selected-creators-placeholder {
  &__description {
  }

  &__link {
  }
}

// Creator snippet ============================================================
.creator-snippet {
  height: 36px;
  padding: 0 11px;
  box-sizing: border-box;
  border-radius: 6px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 1);
  cursor: pointer;

  &--inactive {
    opacity: 0.5;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &--active {
    background: rgba(0, 0, 0, 0.15);
    pointer-events: none;
  }

  &--active:hover {
    background: rgba(0, 0, 0, 0.15);
    pointer-events: none;
  }

  &__checkbox {
    width: 18px;
    height: 18px;
    margin-right: 9px;
  }

  &__avatar {
    width: 23px;
    height: 23px;
    margin-right: 6px;
    transform: scale(1);
    transition: transform 0.05s ease-in-out;
  }

  &:hover &__avatar {
    transform: scale(1.25);
  }

  &__name {
    font-weight: 500;
  }
}

// Select creator form ========================================================
.select-creator-form {
  padding: 21px 0 35px;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    width: calc(100% + 100px);
    height: 100%;
    position: absolute;
    inset: 0 auto auto -50px;
    z-index: -1;
    background: rgba(0, 0, 0, 0.05);
  }

  &__title-and-content-section {
    margin-bottom: 18px;

    &:last-child {
      margin-bottom: 0;
    }

    &--disabled {
      opacity: 0.5;
    }
  }

  &__title {
    margin-bottom: 16px;
  }

  &__creators-list-wrap {
    position: relative;
    z-index: 0;

    &::before,
    &::after {
      content: "";
      width: calc(100% - 20px);
      height: 20px;
      position: absolute;
      inset: 0 auto auto 0;
      z-index: 2;
      opacity: 0;
      background: linear-gradient(0deg, rgba(240, 240, 240, 0) 0%, rgba(240, 240, 240, 1) 100%);
      transition: opacity 0.07s ease-in-out;
      pointer-events: none;
    }
    &::after {
      inset: auto auto 0 0;
      background: linear-gradient(0deg, rgba(240, 240, 240, 1) 0%, rgba(240, 240, 240, 0) 100%);
    }

    &--with-top-curtain {
      &::before {
        opacity: 1;
      }
    }

    &--with-bottom-curtain {
      &::after {
        opacity: 1;
      }
    }
  }

  &__creators-list {
    @include subtleScrollbar;
    max-height: 240px;
    gap: 4px 11px;
    padding: 0 12px 0 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    list-style: none;
    position: relative;
    overflow-y: scroll;
  }

  &__creator-snippet {
    width: calc(50% - 6px);
  }

  &__or-divider {
    width: 100%;
    height: 40px;
    margin-bottom: 5px;
    position: relative;
    z-index: 0;

    &::before {
      content: "";
      width: 100%;
      height: 0;
      border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
      position: absolute;
      inset: 50% auto auto 0;
    }

    &::after {
      content: "or";
      width: 41px;
      height: 41px;
      padding: 0 0 2px;
      box-sizing: border-box;
      border: 1px rgba(0, 0, 0, 0.2) solid;
      border-radius: 1000px;
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      inset: 0 auto auto calc(50% - 20px);
      color: rgba(0, 0, 0, 1);
      font:
        600 19px/19px "Quicksand",
        sans-serif;
      background: rgba(255, 255, 255, 1);
    }
  }

  &__invite-email-input {
    max-width: 540px;
  }
}

// Select creator step ========================================================
.select-creator-step {
  &__title {
    margin-bottom: 27px;
  }

  &__select-creator-form {
    margin-bottom: 25px;
  }

  &__how-it-works {
  }

  &__important-note {
  }
}
</style>
